
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { addMonths, format } from 'date-fns';
import PageLayout from '@/components/layout/PageLayout';
import { SectionHeading } from '@/components/ui/section-heading';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import BeltDisplay from '@/components/ui/belt-display';
import { getEvents, type Event } from "@/integrations/supabase/client";

type ClassType = 'all' | 'lil-dragons' | 'cadets' | 'teens-adults' | 'specialty';

interface ClassSchedule {
  id: number;
  name: string;
  day: string;
  time: string;
  duration: string;
  type: ClassType;
  instructor: string;
  location: string;
  belts?: string;
}

const classes: ClassSchedule[] = [
  // Monday
  {
    id: 1,
    name: "Teens & Adults (Ages 13+)",
    day: "Monday",
    time: "18:00",
    duration: "45 min",
    type: "teens-adults",
    instructor: "Master Juliet",
    location: "Main Dojang",
    belts: "White, Yellow, Orange, Light Blue, Green, Purple, Brown, Red"
  },
  {
    id: 2,
    name: "Teens & Adults (Ages 13+)",
    day: "Monday",
    time: "18:45",
    duration: "45 min",
    type: "teens-adults",
    instructor: "Master Charlton",
    location: "Main Dojang",
    belts: "Navy, Grey"
  },
  {
    id: 3,
    name: "Black Belt Training (All Ages)",
    day: "Monday",
    time: "19:30",
    duration: "45 min",
    type: "teens-adults",
    instructor: "Master Juliet",
    location: "Main Dojang",
    belts: "Black"
  },
  
  // Tuesday
  
  {
    id: 4,
    name: "Lil' Dragons (Ages 4-7)",
    day: "Tuesday",
    time: "17:15",
    duration: "40 min",
    type: "lil-dragons",
    instructor: "Miss Fil",
    location: "Main Dojang",
    belts: "Dragon"
  },
  {
    id: 5,
    name: "Cadets (Ages 8-12)",
    day: "Tuesday",
    time: "18:00",
    duration: "45 min",
    type: "cadets",
    instructor: "Mr Stuart",
    location: "Main Dojang",
    belts: "White, Yellow, Orange, Light Blue"
  },
  {
    id: 6,
    name: "Cadets (Ages 8-12)",
    day: "Tuesday",
    time: "18:45",
    duration: "30 min",
    type: "cadets",
    instructor: "Master Charlton",
    location: "Main Dojang",
    belts: "Green, Purple, Brown, Red"
  },
  {
    id: 7,
    name: "Cadets (Ages 8-12) - Inc. Sparring",
    day: "Tuesday",
    time: "19:15",
    duration: "30 min",
    type: "cadets",
    instructor: "Master Charlton",
    location: "Main Dojang",
    belts: "Green, Purple, Brown, Red, Navy, Grey, Black"
  },
  {
    id: 8,
    name: "Cadets (Ages 8-12)",
    day: "Tuesday",
    time: "19:45",
    duration: "30 min",
    type: "cadets",
    instructor: "Master Charlton",
    location: "Main Dojang",
    belts: "Navy, Grey, Black"
  },
  
  // Wednesday
  {
    id: 9,
    name: "Teens & Adults (Ages 13+)",
    day: "Wednesday",
    time: "18:00",
    duration: "45 min",
    type: "teens-adults",
    instructor: "Miss Fil",
    location: "Main Dojang",
    belts: "White, Yellow, Orange, Light Blue, Green, Purple, Brown, Red"
  },
  {
    id: 10,
    name: "Teens & Adults (Ages 13+) - Inc. Sparring",
    day: "Wednesday",
    time: "18:45",
    duration: "45 min",
    type: "teens-adults",
    instructor: "Master Juliet",
    location: "Main Dojang",
    belts: "Navy, Grey"
  },
  {
    id: 11,
    name: "Black Belt Training (All Ages)",
    day: "Wednesday",
    time: "19:30",
    duration: "45 min",
    type: "teens-adults",
    instructor: "Master Charlton",
    location: "Main Dojang",
    belts: "Black"
  },
  
  // Thursday
  {
    id: 12,
    name: "Lil' Dragons (Ages 4-7)",
    day: "Thursday",
    time: "17:15",
    duration: "40 min",
    type: "lil-dragons",
    instructor: "Mr Stuart",
    location: "Main Dojang",
    belts: "Dragon"
  },
  {
    id: 13,
    name: "Cadets (Ages 8-12)",
    day: "Thursday",
    time: "18:00",
    duration: "45 min",
    type: "cadets",
    instructor: "Miss Fil",
    location: "Main Dojang",
    belts: "White, Yellow, Orange, Light Blue"
  },
  {
    id: 14,
    name: "Cadets (Ages 8-12)",
    day: "Thursday",
    time: "18:45",
    duration: "45 min",
    type: "cadets",
    instructor: "Master Juliet",
    location: "Main Dojang",
    belts: "Green, Purple, Brown, Red"
  },
  {
    id: 15,
    name: "Cadets (Ages 8-12)",
    day: "Thursday",
    time: "19:30",
    duration: "45 min",
    type: "cadets",
    instructor: "Master Juliet",
    location: "Main Dojang",
    belts: "Navy, Grey, Black"
  },
  
  // Saturday AM
  {
    id: 16,
    name: "Lil' Dragons (Ages 4-7)",
    day: "Saturday AM",
    time: "10:00",
    duration: "40 min",
    type: "lil-dragons",
    instructor: "Master Charlton",
    location: "Main Dojang",
    belts: "Dragon"
  },
  {
    id: 17,
    name: "Taekwondo (All Ages)",
    day: "Saturday AM",
    time: "10:45",
    duration: "45 min",
    type: "all",
    instructor: "Master Juliet",
    location: "Main Dojang",
    belts: "White, Yellow, Orange, Light Blue"
  },
  {
    id: 18,
    name: "Poomsae - Patterns (All Ages)",
    day: "Saturday AM",
    time: "11:30",
    duration: "30 min",
    type: "specialty",
    instructor: "Miss Fil",
    location: "Main Dojang",
    belts: "White, Yellow, Orange, Light Blue, Green, Purple, Brown, Red, Navy, Grey, Black"
  },
  
  // Saturday PM
  {
    id: 19,
    name: "Taekwondo (All Ages)",
    day: "Saturday PM",
    time: "12:00",
    duration: "45 min",
    type: "all",
    instructor: "Master Charlton",
    location: "Main Dojang",
    belts: "Green, Purple, Brown, Red, Navy, Grey, Black "
  },
  {
    id: 20,
    name: "Weapons Training (All Ages)",
    day: "Saturday PM",
    time: "12:45",
    duration: "30 min",
    type: "specialty",
    instructor: "Mr Stuart",
    location: "Main Dojang",
    belts: "White, Yellow, Orange, Light Blue, Green, Purple, Brown, Red, Navy, Grey, Black"
  },
  {
    id: 21,
    name: "Black Belt Training (All Ages)",
    day: "Saturday PM",
    time: "13:15",
    duration: "60 min",
    type: "specialty",
    instructor: "Master Juliet",
    location: "Main Dojang",
    belts: "Black"
  }
];

const classesByDay = classes.reduce<Record<string, ClassSchedule[]>>((acc, cls) => {
  if (!acc[cls.day]) {
    acc[cls.day] = [];
  }
  acc[cls.day].push(cls);
  return acc;
}, {});

const daysOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday AM", "Saturday PM", "Sunday"];

const Schedule = () => {
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<ClassType>('all');
  const [nextBlackBeltLessonDate, setNextBlackBeltLessonDate] = useState<string | null>(null);
  const [isLoadingNextBlackBelt, setIsLoadingNextBlackBelt] = useState(false);
  
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam && ['lil-dragons', 'cadets', 'teens-adults', 'specialty'].includes(filterParam)) {
      setFilter(filterParam as ClassType);
    }
  }, [searchParams]);

  // Fetch the next scheduled "Black Belt Lesson" event for the Saturday PM Black Belt class
  useEffect(() => {
    const fetchNextBlackBeltLesson = async () => {
      try {
        setIsLoadingNextBlackBelt(true);

        const now = new Date();
        const end = addMonths(now, 12); // look up to 12 months ahead

        const { data, error } = await getEvents(now, end);

        if (error) {
          console.error("Error fetching next Black Belt Lesson event:", error);
          setNextBlackBeltLessonDate(null);
          return;
        }

        if (data && data.length > 0) {
          const upcoming = (data as Event[])
            .filter((evt) => {
              if (!evt.start_time) return false;
              const start = new Date(evt.start_time);
              return (
                evt.event_type === "Black Belt Lesson" &&
                start >= now
              );
            })
            .sort(
              (a, b) =>
                new Date(a.start_time).getTime() -
                new Date(b.start_time).getTime(),
            )[0];

          if (!upcoming) {
            setNextBlackBeltLessonDate(null);
            return;
          }

          const date = new Date(upcoming.start_time);
          setNextBlackBeltLessonDate(format(date, "EEEE, dd MMM yyyy"));
        } else {
          setNextBlackBeltLessonDate(null);
        }
      } catch (err) {
        console.error("Unexpected error fetching next Black Belt Lesson event:", err);
        setNextBlackBeltLessonDate(null);
      } finally {
        setIsLoadingNextBlackBelt(false);
      }
    };

    fetchNextBlackBeltLesson();
  }, []);

  const getFilteredClasses = (classes: ClassSchedule[], filter: ClassType): ClassSchedule[] => {
    if (filter === 'all') {
      return classes;
    }
    
    return classes.filter(cls => {
      if (filter === 'lil-dragons') {
        return cls.name === "Lil' Dragons (Ages 4-7)" && cls.type === 'lil-dragons';
      }
      
      if (filter === 'specialty') {
        return cls.type === 'specialty';
      }
      
      // Updated logic for teens-adults and cadets to include specialty classes for their respective age groups
      if (filter === 'teens-adults') {
        // Include specialty classes that are for "all ages" or specifically mention teens/adults
        if (cls.type === 'specialty' && 
            (cls.name.toLowerCase().includes('all ages') || 
             cls.name.toLowerCase().includes('black belt'))) {
          return true;
        }
        return cls.type === filter || cls.type === 'all';
      }
      
      if (filter === 'cadets') {
        // Include specialty classes that are for "all ages" or specifically for cadets
        if (cls.type === 'specialty' && 
            (cls.name.toLowerCase().includes('all ages'))) {
          return true;
        }
        return cls.type === filter || cls.type === 'all';
      }
      
      return cls.type === filter || cls.type === 'all';
    });
  };
  
  const filteredClassesByDay = daysOrder.reduce<Record<string, ClassSchedule[]>>((acc, day) => {
    const dayClasses = classesByDay[day] || [];
    const filtered = getFilteredClasses(dayClasses, filter);
    
    if (filtered.length > 0) {
      acc[day] = filtered;
    }
    
    return acc;
  }, {});

  return (
    <PageLayout>
      <div className="pt-16 pb-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">Class Schedule</h1>
            <p className="text-xl text-gray-300">Find the perfect Taekwondo class for you or your child</p>
          </div>
        </div>
      </div>

      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <SectionHeading
              subtitle="Find Your Class"
              title="Weekly Schedule"
              gradient={true}
            />
            
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Button
                onClick={() => setFilter('all')}
                variant={filter === 'all' ? 'default' : 'outline'}
                className={filter === 'all' ? 'bg-taekwondo-red hover:bg-taekwondo-red/90' : ''}
              >
                All Classes
              </Button>
              <Button
                onClick={() => setFilter('lil-dragons')}
                variant={filter === 'lil-dragons' ? 'default' : 'outline'}
                className={filter === 'lil-dragons' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
              >
                Lil' Dragons (Ages 4-7)
              </Button>
              <Button
                onClick={() => setFilter('cadets')}
                variant={filter === 'cadets' ? 'default' : 'outline'}
                className={filter === 'cadets' ? 'bg-taekwondo-blue hover:bg-taekwondo-blue/90' : ''}
              >
                Cadets (Ages 8-12)
              </Button>
              <Button
                onClick={() => setFilter('teens-adults')}
                variant={filter === 'teens-adults' ? 'default' : 'outline'}
                className={filter === 'teens-adults' ? 'bg-taekwondo-red hover:bg-taekwondo-red/90' : ''}
              >
                Teens & Adults (13+)
              </Button>
              <Button
                onClick={() => setFilter('specialty')}
                variant={filter === 'specialty' ? 'default' : 'outline'}
                className={filter === 'specialty' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              >
                Specialty Classes
              </Button>
            </div>
          </div>

          <div className="space-y-8">
            {daysOrder.map(day => {
              const dayClasses = filteredClassesByDay[day];
              if (!dayClasses || dayClasses.length === 0) return null;
              
              return (
                <div key={day} className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="bg-gray-900 text-white py-3 px-6">
                    <h3 className="text-xl font-semibold">{day}</h3>
                  </div>
                  <div className="divide-y">
                    {dayClasses.map(cls => (
                      <div key={cls.id} className="p-4 md:p-6 flex flex-col md:flex-row md:items-center">
                        <div className="md:w-1/4 mb-3 md:mb-0">
                          <div className="flex items-center">
                            <Clock size={18} className="text-taekwondo-red mr-2" />
                            <span className="font-medium">{cls.time}</span>
                            <span className="text-gray-500 ml-2">({cls.duration})</span>
                          </div>
                        </div>
                        <div className="md:w-2/4 mb-3 md:mb-0">
                          <h4 className="font-semibold text-lg flex items-center">
                            <span
                              className={cn(
                                "inline-block w-3 h-3 rounded-full mr-2",
                                cls.type === 'lil-dragons' && "bg-yellow-500",
                                cls.type === 'cadets' && "bg-taekwondo-blue",
                                cls.type === 'teens-adults' && "bg-taekwondo-red",
                                cls.type === 'specialty' && "bg-purple-600",
                                cls.type === 'all' && "bg-green-500"
                              )}
                            ></span>
                            {cls.name}
                          </h4>
                          {/* Special case: Saturday PM Black Belt Training (id: 21) shows next scheduled event */}
                          {cls.id === 21 && nextBlackBeltLessonDate && (
                            <p className="text-sm text-taekwondo-red mt-1">
                              Next Class - {nextBlackBeltLessonDate}
                            </p>
                          )}
                          {/* <div className="text-gray-600 flex items-center mt-1">
                            <Users size={16} className="mr-1" />
                            <span className="text-sm">{cls.instructor}</span>
                          </div> */}
                          {cls.belts && (
                            <div className="text-gray-500 mt-1">
                              <BeltDisplay belts={cls.belts} />
                            </div>
                          )}
                        </div>
                        {/* <div className="md:w-1/4 flex justify-start md:justify-end">
                          <span className="inline-block px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                            {cls.location}
                          </span>
                        </div> */}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-16">
            <SectionHeading
              subtitle="Program Details"
              title="Class Information"
              gradient={true}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-yellow-500">
                <h3 className="text-xl font-bold mb-3">Lil' Dragons (Ages 4-7)</h3>
                <p className="text-gray-700 mb-4">
                  Our Lil' Dragons program is specially designed for our youngest students. Classes focus on 
                  developing basic motor skills, concentration, and following instructions in a fun, 
                  engaging environment.
                </p>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar size={16} className="mr-1" />
                  <span>40-minute classes, 2-3 times per week recommended</span>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-taekwondo-blue">
                <h3 className="text-xl font-bold mb-3">Cadets (Ages 8-12)</h3>
                <p className="text-gray-700 mb-4">
                  Cadet classes build on fundamental Taekwondo techniques while developing discipline, 
                  respect, and self-confidence. Students learn poomsae (forms), basic sparring, and 
                  self-defence applications.
                </p>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar size={16} className="mr-1" />
                  <span>45-60 minute classes, 2-3 times per week recommended</span>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-taekwondo-red">
                <h3 className="text-xl font-bold mb-3">Teens & Adults (Ages 13+)</h3>
                <p className="text-gray-700 mb-4">
                  Our teen and adult program offers comprehensive Taekwondo training focusing on technique 
                  refinement, physical conditioning, and mental development. Classes include advanced forms, 
                  sparring, self-defence, and breaking techniques.
                </p>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar size={16} className="mr-1" />
                  <span>45-minute classes, 2-3 times per week recommended</span>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-purple-600">
                <h3 className="text-xl font-bold mb-3">Specialty Classes</h3>
                <p className="text-gray-700 mb-4">
                  We offer specialised training for students wanting to focus on specific aspects of Taekwondo:
                  Poomsae (forms), Black Belt Training, and Weapons Training. These classes are perfect for 
                  students looking to deepen their practice or prepare for competitions.
                </p>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar size={16} className="mr-1" />
                  <span>30-60 minute classes, supplement to regular training</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 bg-gray-50 p-8 rounded-xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <SectionHeading
                  subtitle="Our Dojang"
                  title="Training Location"
                  alignment="left"
                  className="mb-6"
                  gradient={true}
                />
                <p className="text-gray-700 mb-4">
                  All classes are held at our fully-equipped training facility in Gedling, Nottingham. 
                  Our dojang features padded flooring, training equipment, changing rooms, and a 
                  viewing area for parents and visitors.
                </p>
                <p className="text-gray-700 mb-6">
                  We're conveniently located with ample parking and easy access via public transportation.
                </p>
                <Button size="lg" className="bg-taekwondo-red hover:bg-taekwondo-red/90">
                  <a href="/contact">Get Directions</a>
                </Button>
              </div>
              <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="/images/history/class_photo.jpeg"
                  alt="OMAC Taekwondo dojang" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Schedule;
